<script lang="ts">
  import { STORE } from "../../state.svelte";
  import { shortAddress } from "../../utils";
  import type { PoolInfo, PoolInit } from "sailfish-sdk";
  import PoolBadge from "../elements/PoolBadge.svelte";
  import { RefreshCwIcon } from "lucide-svelte";
  import { onMount } from "svelte";

  const curveTypes = ["PumpFunAmm", "RaydiumLaunchpad"];

  // These read small arrays (max 50) — cheap $derived is OK
  let justCreated = $derived(
    STORE.bot.poolInits.filter((p: PoolInit) => curveTypes.includes(p.pool_type))
  );
  let graduated = $derived(STORE.bot.tokenGraduates);
  let pumpFunPools = $derived(
    justCreated.filter((p: PoolInit) => p.pool_type === "PumpFunAmm")
  );
  let raydiumPools = $derived(
    justCreated.filter((p: PoolInit) => p.pool_type === "RaydiumLaunchpad")
  );

  // Stats computed on interval (not per-event)
  let totalCreated = $state(0);
  let totalGraduated = $state(0);
  let gradRate = $state(0);
  let pumpPct = $state(0);
  let raydiumPct = $state(0);
  let poolsPerMin = $state(0);
  let hottestLaunchpad = $state("PumpFun");

  let poolTimestamps: number[] = [];
  let prevPoolCount = 0;

  onMount(() => {
    const iv = setInterval(() => {
      totalCreated = justCreated.length;
      totalGraduated = graduated.length;
      gradRate = totalCreated + totalGraduated > 0
        ? Math.round((totalGraduated / (totalCreated + totalGraduated)) * 100) : 0;
      pumpPct = totalCreated > 0 ? Math.round((pumpFunPools.length / totalCreated) * 100) : 0;
      raydiumPct = totalCreated > 0 ? Math.round((raydiumPools.length / totalCreated) * 100) : 0;
      hottestLaunchpad = pumpFunPools.length >= raydiumPools.length ? "PumpFun" : "Raydium";

      // Pools/min
      const currentCount = STORE.bot.poolInits.length;
      if (currentCount > prevPoolCount && prevPoolCount > 0) {
        const now = Date.now();
        for (let i = 0; i < currentCount - prevPoolCount; i++) poolTimestamps.push(now);
      }
      prevPoolCount = currentCount;
      const cutoff = Date.now() - 60000;
      poolTimestamps = poolTimestamps.filter(t => t > cutoff);
      poolsPerMin = poolTimestamps.length;
    }, 2000);
    return () => clearInterval(iv);
  });

  let poolInfos: Record<string, PoolInfo> = $state({});

  const updatePoolInfos = () => {
    const allPools = [...STORE.bot.poolInits, ...STORE.bot.tokenGraduates];
    for (const pool of allPools) {
      const info = STORE.bot.sailfish.getPoolInfo(pool.pool_address);
      if (info !== null) {
        poolInfos[pool.pool_address] = info;
      }
    }
  };

  const fetchMetadata = async (poolInit: PoolInit) => {
    const info = await STORE.bot.sailfish.buildPoolInfoFromPoolInit(poolInit);
    if (!(info instanceof Error)) {
      STORE.bot.sailfish.insertPoolInfo(info);
      updatePoolInfos();
    }
  };

  const getPairName = (pool: PoolInit) => {
    const info = poolInfos[pool.pool_address];
    if (info) {
      return `${info.base_token.symbol} / ${info.quote_token.symbol}`;
    }
    return `${shortAddress(pool.token_0_mint, 4, 4)} / ${shortAddress(pool.token_1_mint, 4, 4)}`;
  };
</script>

<div class="px-4">
  <!-- Widgets Row -->
  <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">New Pools</p>
      <p class="text-2xl font-black text-white tabular-nums">{totalCreated}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Graduated</p>
      <p class="text-2xl font-black text-white tabular-nums">{totalGraduated}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Grad. Rate</p>
      <p class="text-2xl font-black text-white tabular-nums">{gradRate}<span class="text-sky-500 text-sm">%</span></p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Pools/min</p>
      <p class="text-2xl font-black text-white tabular-nums">{poolsPerMin}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Hottest</p>
      <p class="text-lg font-black tabular-nums {hottestLaunchpad === 'PumpFun' ? 'text-sky-500' : 'text-blue-500'}">{hottestLaunchpad}</p>
    </div>
  </div>

  <!-- DEX Distribution -->
  {#if totalCreated > 0}
    <div class="mb-6">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-2">Launchpad Distribution</p>
      <div class="h-2 bg-gray-800 rounded-full flex overflow-hidden mb-2">
        <div class="bg-[#00d18c] transition-all duration-500" style="width: {pumpPct}%"></div>
        <div class="bg-[#6c5ce7] transition-all duration-500" style="width: {raydiumPct}%"></div>
      </div>
      <div class="flex gap-4 text-[10px] font-mono text-gray-500">
        <span class="flex items-center gap-1"><span class="w-2 h-2 bg-[#00d18c] rounded-full inline-block"></span> PumpFun {pumpFunPools.length}</span>
        <span class="flex items-center gap-1"><span class="w-2 h-2 bg-[#6c5ce7] rounded-full inline-block"></span> Raydium {raydiumPools.length}</span>
      </div>
    </div>
  {/if}

  <!-- Two Column Tables -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
    <!-- Left: Just Created -->
    <div>
      <h3 class="text-[11px] font-mono text-gray-600 uppercase tracking-[0.4em] mb-3">Just Created</h3>
      <div class="overflow-x-auto">
        <table class="w-full text-xs font-mono">
          <thead>
            <tr class="text-gray-600 text-left border-b border-gray-800">
              <th class="py-1.5 px-2 font-normal">DEX</th>
              <th class="py-1.5 px-2 font-normal">Pair</th>
              <th class="py-1.5 px-2 font-normal text-right">Slot</th>
              <th class="py-1 px-2 font-normal w-6"></th>
            </tr>
          </thead>
          <tbody>
            {#each justCreated as pool}
              <tr class="border-b border-gray-800/50 hover:bg-gray-900/50">
                <td class="py-1.5 px-2"><PoolBadge poolType={pool.pool_type} /></td>
                <td class="py-1.5 px-2">
                  <a href={`https://dexscreener.com/solana/${pool.pool_address}`} target="_blank" class="text-white hover:text-sky-500 transition-colors">{getPairName(pool)}</a>
                </td>
                <td class="py-1.5 px-2 text-right text-gray-500 tabular-nums">{pool.tx.slot}</td>
                <td class="py-1 px-2 text-right">
                  <button class="text-gray-700 hover:text-white transition-colors" onclick={() => fetchMetadata(pool)}>
                    <RefreshCwIcon class="size-3" />
                  </button>
                </td>
              </tr>
            {/each}
            {#if justCreated.length === 0}
              <tr><td colspan="4" class="py-6 text-center text-gray-600 italic">Waiting for new pools...</td></tr>
            {/if}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Right: Graduated -->
    <div>
      <h3 class="text-[11px] font-mono text-gray-600 uppercase tracking-[0.4em] mb-3">Graduated</h3>
      <div class="overflow-x-auto">
        <table class="w-full text-xs font-mono">
          <thead>
            <tr class="text-gray-600 text-left border-b border-gray-800">
              <th class="py-1.5 px-2 font-normal">DEX</th>
              <th class="py-1.5 px-2 font-normal">Pair</th>
              <th class="py-1.5 px-2 font-normal text-right">Slot</th>
              <th class="py-1 px-2 font-normal w-6"></th>
            </tr>
          </thead>
          <tbody>
            {#each graduated as pool}
              <tr class="border-b border-gray-800/50 hover:bg-gray-900/50">
                <td class="py-1.5 px-2"><PoolBadge poolType={pool.pool_type} /></td>
                <td class="py-1.5 px-2">
                  <a href={`https://dexscreener.com/solana/${pool.pool_address}`} target="_blank" class="text-white hover:text-sky-500 transition-colors">{getPairName(pool)}</a>
                </td>
                <td class="py-1.5 px-2 text-right text-gray-500 tabular-nums">{pool.tx.slot}</td>
                <td class="py-1 px-2 text-right">
                  <button class="text-gray-700 hover:text-white transition-colors" onclick={() => fetchMetadata(pool)}>
                    <RefreshCwIcon class="size-3" />
                  </button>
                </td>
              </tr>
            {/each}
            {#if graduated.length === 0}
              <tr><td colspan="4" class="py-6 text-center text-gray-600 italic">Waiting for graduations...</td></tr>
            {/if}
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
